import React, { ReactNode } from "react";
import {
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogProps as MuiDialogProps,
} from "@mui/material";

export interface DialogProps extends Omit<MuiDialogProps, "title"> {
  /**
   * The title displayed at the top of the dialog
   */
  title?: string;
  /**
   * The content of the dialog body
   */
  children: ReactNode;
  /**
   * Optional action buttons rendered in the dialog footer
   */
  actions?: ReactNode;
  /**
   * Callback fired when the dialog should close
   */
  onClose?: () => void;
  /**
   * Optional suffix for test IDs. This suffix will be appended to the base test ID "ds-dialog".
   */
  testIdSuffix?: string;
}

/**
 * Base Dialog component that wraps MUI Dialog with consistent styling and behavior
 *
 * Use this instead of MUI Dialog directly to ensure consistency across the application.
 * This component provides sensible defaults while still allowing full customization
 * through MUI Dialog props.
 *
 * @example
 * ```tsx
 * <Dialog
 *   open={isOpen}
 *   title="Confirm Action"
 *   onClose={handleClose}
 *   actions={
 *     <>
 *       <Button onClick={handleClose}>Cancel</Button>
 *       <Button onClick={handleConfirm} variant="contained">Confirm</Button>
 *     </>
 *   }
 * >
 *   <Typography>Are you sure you want to proceed?</Typography>
 * </Dialog>
 * ```
 */
export const Dialog: React.FC<DialogProps> = ({
  title,
  children,
  actions,
  onClose,
  maxWidth = "sm",
  fullWidth = true,
  testIdSuffix,
  ...props
}) => {
  const testId = `ds-dialog${testIdSuffix ? `-${testIdSuffix}` : ""}`;
  // IDs used for ARIA labels / accessibility
  const titleId = `${testId}-title`;
  const contentId = `${testId}-content`;

  return (
    <MuiDialog
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      data-testid={testId}
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={contentId}
      {...props}
    >
      {title && (
        <DialogTitle
          id={titleId}
          data-testid={testId ? `${testId}-title` : undefined}
        >
          {title}
        </DialogTitle>
      )}
      <DialogContent
        id={contentId}
        data-testid={testId ? `${testId}-content` : undefined}
      >
        {children}
      </DialogContent>
      {actions && (
        <DialogActions data-testid={testId ? `${testId}-actions` : undefined}>
          {actions}
        </DialogActions>
      )}
    </MuiDialog>
  );
};

Dialog.displayName = "Dialog";
